<script>
  import { createEventDispatcher, tick } from "svelte";
  import { parsePhoneNumberWithError, ParseError } from "libphonenumber-js/max";
  import { normalizeTelInput } from "svelte-tel-input";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import { formatPhoneNumber, toE164 } from "../phoneUtils.js";

  const dispatch = createEventDispatcher();

  export let value = ""; // Stored as E.164 (e.g. +61499999999)
  export let id = "phone-input-" + Math.random().toString(36).slice(2, 9);
  export let name = "phone";
  export let placeholder = "0412 345 678";
  export let disabled = false;
  export let readonly = false;
  export let required = false;
  export let valid = true;
  export let country = "AU";
  export let showIcon = true;
  export let classes = "";
  export { classes as class };

  let inputEl;
  let displayValue = "";
  let lastExternalValue = null;
  let detailedValue = null;

  /**
   * Formats phone input as-you-type according to Australian standards,
   * guaranteeing the leading '0' is never dropped.
   * Mobile: 04xx xxx xxx
   * Landline: 0x xxxx xxxx (02, 03, 07, 08)
   */
  function formatAsYouType(raw) {
    if (!raw) return "";
    const str = String(raw).trim();
    if (!str) return "";

    // If international starts with +61
    if (str.startsWith("+61")) {
      const national = "0" + str.slice(3).replace(/\D/g, "");
      return formatNationalDigits(national);
    }
    if (str.startsWith("+")) {
      return str;
    }

    let digits = str.replace(/\D/g, "");
    if (!digits) return "";

    // If user starts typing without the leading zero (e.g. 414... or 2...), auto-insert 0
    if (digits.length >= 1 && (digits[0] === "4" || digits[0] === "2" || digits[0] === "3" || digits[0] === "7" || digits[0] === "8")) {
      digits = "0" + digits;
    }

    return formatNationalDigits(digits);
  }

  function formatNationalDigits(digits) {
    if (!digits) return "";
    // Mobile: starts with 04 -> 04xx xxx xxx
    if (digits.startsWith("04")) {
      if (digits.length <= 4) return digits;
      if (digits.length <= 7) return digits.slice(0, 4) + " " + digits.slice(4);
      return digits.slice(0, 4) + " " + digits.slice(4, 7) + " " + digits.slice(7, 10);
    }

    // Landline: starts with 0 (e.g. 02, 03, 07, 08) -> 0x xxxx xxxx
    if (digits.startsWith("0")) {
      if (digits.length <= 2) return digits;
      if (digits.length <= 6) return digits.slice(0, 2) + " " + digits.slice(2);
      return digits.slice(0, 2) + " " + digits.slice(2, 6) + " " + digits.slice(6, 10);
    }

    return digits.slice(0, 10);
  }

  function findNewCursorPosition(oldVal, newVal, oldCursor) {
    if (oldCursor >= oldVal.length) {
      return newVal.length;
    }
    let fvIndex = 0;
    for (let nvIndex = 0; nvIndex < oldCursor; nvIndex++) {
      const c = oldVal[nvIndex];
      if (c >= "0" && c <= "9") {
        while (fvIndex < newVal.length && !(newVal[fvIndex] >= "0" && newVal[fvIndex] <= "9")) {
          fvIndex++;
        }
        fvIndex++;
      }
    }
    return fvIndex;
  }

  function validateAndParse(val) {
    if (!val || !val.trim()) {
      valid = !required;
      detailedValue = null;
      return;
    }
    try {
      const parsed = parsePhoneNumberWithError(val, country || "AU");
      detailedValue = normalizeTelInput(parsed);
      valid = !!detailedValue?.isValid;
    } catch (err) {
      if (err instanceof ParseError) {
        detailedValue = { isValid: false, error: err.message };
      } else {
        detailedValue = { isValid: false, error: "Invalid number" };
      }
      valid = false;
    }
  }

  // React to external value changes (e.g. database load or prop updates)
  $: if (value !== lastExternalValue) {
    lastExternalValue = value;
    const formatted = value ? formatPhoneNumber(value) : "";
    if (formatted !== displayValue) {
      displayValue = formatted;
    }
    validateAndParse(value);
  }

  function updateValuesFromFormatted(formatted) {
    if (!formatted || !formatted.trim()) {
      value = "";
      valid = !required;
      lastExternalValue = "";
      detailedValue = null;
      dispatch("change", { value: "", valid, detailedValue: null });
      return;
    }

    const e164Val = toE164(formatted);
    value = e164Val;
    lastExternalValue = value;
    validateAndParse(e164Val);

    dispatch("change", {
      value: e164Val,
      national: formatted,
      valid,
      detailedValue
    });
  }

  async function handleInput(event) {
    const el = event.target;
    const rawVal = el.value;
    const initialCursor = el.selectionStart || 0;

    const formatted = formatAsYouType(rawVal);
    displayValue = formatted;

    await tick();
    if (inputEl) {
      const newCursor = findNewCursorPosition(rawVal, formatted, initialCursor);
      inputEl.selectionStart = newCursor;
      inputEl.selectionEnd = newCursor;
    }

    updateValuesFromFormatted(formatted);
  }

  function handleKeyDown(event) {
    if (event.key === "Backspace") {
      const el = event.target;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      // If single cursor position and previous character is a space, delete the digit before the space
      if (start === end && start > 1 && el.value[start - 1] === " ") {
        event.preventDefault();
        const newVal = el.value.slice(0, start - 2) + el.value.slice(start);
        const formatted = formatAsYouType(newVal);
        displayValue = formatted;
        const newPos = start - 2;
        tick().then(() => {
          if (inputEl) {
            inputEl.selectionStart = newPos;
            inputEl.selectionEnd = newPos;
          }
        });
        updateValuesFromFormatted(formatted);
      }
    }
  }

  function handleBlur() {
    if (value) {
      displayValue = formatPhoneNumber(value);
    }
    dispatch("blur", { value, valid });
  }
</script>

<div class="tt-phone-input-wrapper {classes}" class:is-disabled={disabled} class:is-readonly={readonly}>
  {#if showIcon}
    <div class="phone-icon-slot">
      <Phone size={14} class="phone-ico" />
    </div>
  {/if}

  <input
    {...$$restProps}
    bind:this={inputEl}
    {id}
    {name}
    {placeholder}
    {disabled}
    {readonly}
    {required}
    type="tel"
    autocomplete="tel"
    value={displayValue}
    class="tt-tel-control {showIcon ? 'has-left-icon' : ''}"
    on:input={handleInput}
    on:keydown={handleKeyDown}
    on:blur={handleBlur}
    on:focus
  />
</div>

<style>
  .tt-phone-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
  }

  .phone-icon-slot {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  :global(.tt-phone-input-wrapper .tt-tel-control) {
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
    font-size: 0.82rem;
    color: var(--tt-slate-900, #0f172a);
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    padding: 7px 10px;
    outline: none;
    line-height: 1.4;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  :global(.tt-phone-input-wrapper .tt-tel-control.has-left-icon) {
    padding-left: 28px;
  }

  :global(.tt-phone-input-wrapper .tt-tel-control:focus) {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  :global(.tt-phone-input-wrapper .tt-tel-control:disabled) {
    background-color: var(--tt-slate-50, #f8fafc);
    color: var(--tt-slate-400, #94a3b8);
    cursor: not-allowed;
  }

  :global(.tt-phone-input-wrapper .tt-tel-control::placeholder) {
    color: var(--tt-slate-400, #94a3b8);
    opacity: 1;
  }
</style>
